
import { defineComponent, ref } from 'vue';
import { pushModal, container } from 'jenesius-vue-modal';
import InvitationsModal from '@/modals/InvitationsModal.vue';
import TeamModal from '@/modals/TeamModal.vue';
import CreateTeamModal from '@/modals/TeamCreateModal.vue';
import { LottieAnimation } from 'lottie-web-vue';
import useRequest from '@/composables/useRequest';
import { GetTeamStatusRequest } from '@/data/requests/GetTeamStatusRequest';
import { useI18n } from 'vue-i18n';
import { TeamStatusRequestResponse } from '@/data/models/TeamStatusRequestResponse';
import { TeamInvite } from '@/data/models/TeamInvite';
import { TeamInviteRequestResponse } from '@/data/models/TeamInviteRequestResponse';
import useEventStatus from '@/composables/useEventStatus';
import store from '@/data/store';
import { OAuthToken } from '@/data/models/OAuthToken';
import useFormatDateToString from '@/composables/useFormatDateToString';

export default defineComponent({
  setup() {
    const { t } = useI18n();

    const { isLive, hasEnded } = useEventStatus();

    const sessionInfo = store.getters.sessionInfo as OAuthToken | undefined;

    const invitesCount = ref(0);
    const teamName = ref<string | undefined>();
    const invites = ref<TeamInvite[] | undefined>();
    const showTestDateChanger = ref(false);
    const testDate = ref(useFormatDateToString(store.state.testDate));

    const { loading, error, execute } = useRequest<GetTeamStatusRequest, TeamStatusRequestResponse>(
      new GetTeamStatusRequest(),
      t('errors.team.cannot_get_status')
    );

    const refreshHomePage = async () => {
      await execute().then((response) => {
        invitesCount.value = 0;
        teamName.value = undefined;
        invites.value = undefined;

        if (response.invites !== undefined) invitesCount.value = response.invites.length;

        if (response.team?.team_name !== undefined) teamName.value = response.team.team_name;

        if (response.invites !== undefined) {
          invites.value = [];

          response.invites.forEach((invite: TeamInviteRequestResponse) => {
            invites.value?.push({
              id: invite.id,
              team_name: invite.team_name,
            });
          });
        }
      });
    };

    refreshHomePage();

    const openInvitationsModal = () => {
      if (!hasEnded) {
        if (invitesCount.value > 0) {
          pushModal(InvitationsModal, {
            hasTeam: (function () {
              return teamName.value !== undefined;
            })(),
            invites: invites,
            refreshHomePage: refreshHomePage,
          });
        }
      }
    };

    const openTeamModal = () => {
      if (!hasEnded) {
        pushModal(TeamModal, {
          teamName: teamName,
          refreshHomePage: refreshHomePage,
        });
      }
    };

    const openCreateTeamModal = async () => {
      const createTeamModal = await pushModal(CreateTeamModal, { refreshHomePage: refreshHomePage });

      createTeamModal.on('onCreateTeam', refreshHomePage);
    };

    const changeTestDate = () => {
      store.commit('setTestDate', { date: new Date(testDate.value) });
    };

    const enableTestDateChanger = () => {
      if (store.state.isTest) {
        showTestDateChanger.value = true;
      }
    };

    return {
      invitesCount,
      teamName,
      loading,
      error,
      openInvitationsModal,
      openTeamModal,
      openCreateTeamModal,
      isLive,
      hasEnded,
      sessionInfo,
      showTestDateChanger,
      testDate,
      changeTestDate,
      enableTestDateChanger,
    };
  },

  components: { LottieAnimation, WidgetContainerModal: container },
});
